import { useCallback, useEffect, useRef, useState } from 'react'
import { Box, Group, Text } from '@mantine/core'
import { useFormat } from '../../_units'
import { useI18n } from '../../_i18n'
import { DirectionArrow } from '../_tiles'
import { compassFromDeg, windArrowDeg } from './weatherData'
import { scalePos, gustHatch, WIND_SEVERITY_GRADIENT } from './windScale'
import { MONO_FONT, DATA_FZ } from './railStyle'

// Rough rendered-width estimates (CSS px), used only to decide whether two
// adjacent labels have room to sit independently or need to collapse into
// one cluster — a fixed % threshold can't work here since this card renders
// anywhere from a ~340px mobile band to a ~650px desktop column, so these are
// converted against the real measured track width (see trackWidth below).
// A trailing gust word/suffix never factors into these — it always flows
// freely after its anchor rather than being centered itself, so only the
// primary "N kt" footprint matters here.
const LABEL_W = 40 // "N kt"
const PAIR_W = 96 // "N kt - N kt"
const GUST_W = 96 // "(N kt gusts)" — the gust always wears its parentheses
const HYPHEN_W = 8 // the en dash itself
const LABEL_MARGIN = 6 // breathing room between two adjacent anchors
const EDGE_MARGIN = 4 // breathing room between a label and the track edge
const TRAIL_GAP = 6 // gap between a primary label and its trailing suffix
const HYPHEN_GAP = 5 // gap either side of the hyphen in a centered "low - high" pair

// Low and high stay at their own positions only while there is room for the
// dash BETWEEN their rendered edges — the dash and its two gaps. The dash is
// not optional (it is what says the two numbers are one range), so the moment
// it stops fitting the pair collapses into one flowed block instead. Reserving
// its width here is what makes "always present" safe: without it, a spread
// that just cleared the old threshold would render the dash straight through
// the labels either side.
const DASH_ROOM_PX = HYPHEN_W + 2 * HYPHEN_GAP

// Resolves how to anchor a block whose true track-position span is
// [leftTrue, rightTrue] (equal for a single point) and whose own estimated
// width is widthPx: centered on that span by default, but left/right-
// justified against the true position of whichever end is clipping — never
// against the block's midpoint, so e.g. a low value near 0% still renders
// flush against the true left edge instead of sliding right toward center.
// rightEdgePct is a same-estimate fallback used only until the real DOM
// measurement below is available.
function resolveAnchor(leftTrue, rightTrue, widthPx, trackWidth) {
  const center = (leftTrue + rightTrue) / 2
  const halfPct = ((widthPx / 2 + EDGE_MARGIN) / trackWidth) * 100
  if (center - halfPct < 0) {
    return {
      pos: leftTrue,
      transform: 'translateX(0)',
      rightEdgePct: leftTrue + (widthPx / trackWidth) * 100,
    }
  }
  if (center + halfPct > 100) {
    return { pos: rightTrue, transform: 'translateX(-100%)', rightEdgePct: rightTrue }
  }
  return {
    pos: center,
    transform: 'translateX(-50%)',
    rightEdgePct: center + (widthPx / 2 / trackWidth) * 100,
  }
}

// Where a resolved anchor's LEFT edge lands, as a % of the track, from the same
// width estimate resolveAnchor used. Together with rightEdgePct this is what
// the merge decisions compare — rendered edges, not raw scale positions.
// The difference only shows at the ends of the track: a label that clips is
// justified inward, so its far edge sits half a label closer to its neighbour
// than its raw position suggests. A hurricane-force gust (64+ kt is always
// 100%) is the everyday case: right-justified against the track end, its
// bracketed block reaches back over a high value that a raw-gap test had
// judged comfortably clear.
function leftEdgeOf(anchor, widthPx, trackWidth) {
  const widthPct = (widthPx / trackWidth) * 100
  if (anchor.transform === 'translateX(0)') return anchor.pos
  if (anchor.transform === 'translateX(-100%)') return anchor.pos - widthPct
  return anchor.pos - widthPct / 2
}

// Measures where a DOM node's right edge actually lands, as a % of a
// container's width — used to give a trailing gust suffix accurate "reasonable
// min-spacing" off the label it follows instead of the width *estimates*
// above (which are close enough to decide layout, but not pixel-accurate
// enough to place adjacent text tightly). A *callback* ref, not a plain
// object ref: which element this measures can change across renders (the
// track re-measures itself shortly after mount, which can flip which layout
// branch below is active, moving the ref to a different label entirely) —
// only a callback ref re-fires and re-measures on that kind of reattachment;
// an effect keyed once on mount would silently keep measuring the old node.
function useRightEdgePct(containerRef) {
  const [pct, setPct] = useState(null)
  const cleanupRef = useRef(null)
  const refCallback = useCallback(
    (el) => {
      if (cleanupRef.current) {
        cleanupRef.current()
        cleanupRef.current = null
      }
      if (!el) {
        setPct(null)
        return
      }
      const update = () => {
        const container = containerRef.current
        if (!container) return
        const elRect = el.getBoundingClientRect()
        const containerRect = container.getBoundingClientRect()
        if (containerRect.width === 0) return
        setPct(((elRect.right - containerRect.left) / containerRect.width) * 100)
      }
      update()
      const ro = new ResizeObserver(update)
      ro.observe(el)
      if (containerRef.current) ro.observe(containerRef.current)
      cleanupRef.current = () => ro.disconnect()
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )
  return [refCallback, pct]
}

// Fixed marine-scale wind chart, ported from the design handoff
// (docs/design_handoff_marine_forecast/): a 0-64+kt severity gradient painted
// once and revealed (clip-path) between the day's min and max sustained wind,
// a 45deg gust hatch from sustained to gust speed, and axis-label numbers
// colored to match the gradient beneath them. Lives on the Week section's
// solid TILE.mid band ("same blues in light + dark" per the app's tile
// convention), so chrome is fixed to the dark-appropriate palette rather than
// following the site's own light/dark toggle.
//
// Axis labels. Two things are invariant, and everything else is about where
// they go rather than whether they appear:
//
//   • the en dash between low and high is ALWAYS drawn — it is what says the
//     two numbers are one range rather than two readings;
//   • the gust is ALWAYS parenthesised — it qualifies the sustained wind, and
//     the brackets say so wherever it ends up sitting.
//
// Given room, low, the dash and high each sit at their own true position, the
// dash centred on the midpoint of the active band. Too tight for that and the
// three collapse into one flowed "low - high" block, the dash still centred on
// the pair's own true span, with low and high flowing outward from it — never
// dragged off-position by a trailing gust.
//
// The gust follows the same idea: centred on its own place on the scale when
// it fits there, otherwise flowed after the sustained value at that label's
// actual measured edge (not an estimate). Both readings are true; the
// difference is only whether the number is standing over the track position
// it describes.
//
// A trailing gust that would run past the end of the track pulls its whole
// block in instead: the sustained value and its bracketed gust render as one
// flowed group, right-justified against the track edge, so the gust is never
// clipped ("51 kt (68 kt gu" — Waimea Bay, Sep 2026). If that pulled-in group
// would then crowd the low value, the three collapse into the flowed
// "low - high" pair with the gust after it, the whole line flush right.
//
// "Room" is judged between the edges each label would actually render at —
// resolveAnchor first, then compare — not between raw scale positions. The
// two agree whenever every label is centred, but a label at either end of the
// track is justified inward and its edge moves half a label toward its
// neighbour. Hurricane-force gusts made that visible: 64+ kt is always 100%,
// so the gust is right-justified and its bracket reached back over a 50 kt
// high that the raw gap said had room (Waimea Bay, Sep 2026).
export default function WindSpeedChart({ wmin, wsust, gmax, windDeg }) {
  const { t } = useI18n()
  const fmt = useFormat()
  const axisRef = useRef(null)
  const [trackWidth, setTrackWidth] = useState(360)
  const [primaryRef, measuredRightEdgePct] = useRightEdgePct(axisRef)

  useEffect(() => {
    const el = axisRef.current
    if (!el) return
    const update = () => setTrackWidth(el.offsetWidth || 360)
    update()
    const ro = new ResizeObserver(update)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const pxToPct = (px) => (px / trackWidth) * 100

  const trackBg = 'rgba(255,255,255,.1)'
  const gridColors = ['.11', '.11', '.13', '.15']
  const hyphenColor = '#a8c3ce'

  const minRaw = scalePos(wmin)
  const sustRaw = scalePos(wsust)
  // Only worth a gust label when it actually reads differently from the
  // sustained number — 13.4 vs 12.6 both round to "13 kt".
  const gustShown = gmax != null && Math.round(gmax) > Math.round(wsust)
  const gustRaw = gustShown ? scalePos(gmax) : null

  // Severity color stays on the track/hatch below; as *text* on this card's
  // background it fails WCAG contrast for several ramp stops (measured as low
  // as ~1.5:1), so the numbers themselves are plain white like the rest of
  // the rail.
  const lowColor = '#fff'
  const highColor = '#fff'
  const gustColor = '#fff'

  const lowLabel = fmt.wind(wmin)
  const highLabel = fmt.wind(wsust)
  const gustLabel = gustShown ? fmt.wind(gmax) : null
  const gustsWord = t('gusts').toLowerCase()

  // Resolve where each label would stand on its own first; the merge
  // decisions below compare those rendered edges.
  const lowAnchor = resolveAnchor(minRaw, minRaw, LABEL_W, trackWidth)
  const highAnchor = resolveAnchor(sustRaw, sustRaw, LABEL_W, trackWidth)
  const lowHighAlone =
    leftEdgeOf(highAnchor, LABEL_W, trackWidth) - lowAnchor.rightEdgePct < pxToPct(DASH_ROOM_PX)
  const pairAnchorAlone = lowHighAlone ? resolveAnchor(minRaw, sustRaw, PAIR_W, trackWidth) : null
  // The gust decision needs to know how wide the thing to its left actually
  // is — when low+high already merged into the wider "N kt - N kt" pair, a
  // gap that would've been plenty next to a solo "N kt" isn't enough next to
  // that pair, or the trailing gust ends up glued flush against it. Comparing
  // the pair anchor's own right edge covers that by construction.
  const leftOfGust = lowHighAlone ? pairAnchorAlone : highAnchor
  // GUST_W, not LABEL_W: a standalone gust renders the whole parenthesised
  // "(N kt gusts)", so it needs that much room to hold its own position rather
  // than the width of the bare number it used to show there.
  const gustAnchor = gustShown ? resolveAnchor(gustRaw, gustRaw, GUST_W, trackWidth) : null
  const highGustMerge =
    gustShown &&
    leftEdgeOf(gustAnchor, GUST_W, trackWidth) - leftOfGust.rightEdgePct < pxToPct(LABEL_MARGIN)

  // A trailing gust that would run past the track end pulls its whole block
  // (the label it follows plus the bracketed gust) in to sit flush against
  // the right edge. When the block being pulled in is a lone high value and
  // its new left edge would land on the low label, the three collapse into
  // the flowed pair first — which then overruns by construction (it is wider
  // still) and rights-justifies as a whole. Decided once here so the branch
  // selection below is a plain lookup.
  const trailOverflows = (leftBlock) =>
    leftBlock.rightEdgePct + pxToPct(TRAIL_GAP + GUST_W) > 100 - pxToPct(EDGE_MARGIN)
  let lowHighMerge = lowHighAlone
  let pairAnchor = pairAnchorAlone
  let blockFlush = false
  if (highGustMerge && !lowHighMerge && trailOverflows(highAnchor)) {
    blockFlush = true
    const blockLeftPct = 100 - pxToPct(LABEL_W + TRAIL_GAP + GUST_W)
    if (blockLeftPct - lowAnchor.rightEdgePct < pxToPct(DASH_ROOM_PX)) {
      lowHighMerge = true
      pairAnchor = resolveAnchor(minRaw, sustRaw, PAIR_W, trackWidth)
    }
  } else if (highGustMerge && lowHighMerge && trailOverflows(pairAnchor)) {
    blockFlush = true
  }

  const GustSuffixText = () => (
    <Text fz={DATA_FZ.label} fw={600} style={{ color: gustColor, fontFamily: MONO_FONT }}>
      ({gustLabel} {gustsWord})
    </Text>
  )

  // Where a trailing bit should start: the real measured right edge of the
  // label it follows when THIS label is the one being measured (measureRef
  // was handed to it), else the width estimate. measuredRightEdgePct is a
  // single value shared across the whole component (only one label per
  // render ever carries a trailing suffix) — gating on `measured` matters
  // because trackWidth corrects itself shortly after mount and can change
  // which branch below is active; without the gate, a stale measurement from
  // a branch this render *isn't even using* could otherwise leak in.
  const trailLeftPct = (anchor, measured) =>
    (
      (measured && measuredRightEdgePct != null ? measuredRightEdgePct : anchor.rightEdgePct) +
      pxToPct(TRAIL_GAP)
    ).toFixed(1)

  // A label anchored at a fixed position, with an optional trailing bit
  // (gust suffix / "gusts" word) that never participates in the anchor's own
  // centering — it just flows immediately after the label's real measured
  // right edge (measureRef), falling back to the anchor's width estimate
  // until that measurement lands.
  const AnchoredLabel = ({ anchor, children, trail, measureRef }) => (
    <>
      <Box
        ref={measureRef}
        style={{
          position: 'absolute',
          left: `${anchor.pos.toFixed(1)}%`,
          top: 0,
          transform: anchor.transform,
          whiteSpace: 'nowrap',
        }}
      >
        {children}
      </Box>
      {trail && (
        <Box
          style={{
            position: 'absolute',
            left: `${trailLeftPct(anchor, Boolean(measureRef))}%`,
            top: 0,
            whiteSpace: 'nowrap',
          }}
        >
          {trail}
        </Box>
      )}
    </>
  )

  // The "low - high" pair. Left/right-justified modes render as one flowing
  // group (already anchored on the true extreme value at that end). Centered
  // mode instead dead-centers the hyphen itself on the pair's true midpoint,
  // with low/high flowing outward from it — centering the whole asymmetric
  // "8 kt - 10 kt" string as one block would otherwise pull the hyphen off
  // the true midpoint whenever low/high render at different widths.
  const Pair = ({ anchor, measureRef }) => {
    if (anchor.transform !== 'translateX(-50%)') {
      return (
        <Box
          ref={measureRef}
          style={{
            position: 'absolute',
            left: `${anchor.pos.toFixed(1)}%`,
            top: 0,
            transform: anchor.transform,
            whiteSpace: 'nowrap',
          }}
        >
          <Group gap={5} wrap="nowrap" align="baseline">
            <Text fz={DATA_FZ.label} fw={600} style={{ color: lowColor, fontFamily: MONO_FONT }}>
              {lowLabel}
            </Text>
            <Text fz={DATA_FZ.label} fw={600} style={{ color: hyphenColor, fontFamily: MONO_FONT }}>
              {t('range_separator_short')}
            </Text>
            <Text fz={DATA_FZ.label} fw={600} style={{ color: highColor, fontFamily: MONO_FONT }}>
              {highLabel}
            </Text>
          </Group>
        </Box>
      )
    }
    const sidePct = pxToPct(HYPHEN_GAP)
    return (
      <>
        <Box
          style={{
            position: 'absolute',
            left: `${(anchor.pos - sidePct).toFixed(1)}%`,
            top: 0,
            transform: 'translateX(-100%)',
            whiteSpace: 'nowrap',
          }}
        >
          <Text fz={DATA_FZ.label} fw={600} style={{ color: lowColor, fontFamily: MONO_FONT }}>
            {lowLabel}
          </Text>
        </Box>
        <Box
          style={{
            position: 'absolute',
            left: `${anchor.pos.toFixed(1)}%`,
            top: 0,
            transform: 'translateX(-50%)',
            whiteSpace: 'nowrap',
          }}
        >
          <Text fz={DATA_FZ.label} fw={600} style={{ color: hyphenColor, fontFamily: MONO_FONT }}>
            {t('range_separator_short')}
          </Text>
        </Box>
        <Box
          ref={measureRef}
          style={{
            position: 'absolute',
            left: `${(anchor.pos + sidePct).toFixed(1)}%`,
            top: 0,
            transform: 'translateX(0)',
            whiteSpace: 'nowrap',
          }}
        >
          <Text fz={DATA_FZ.label} fw={600} style={{ color: highColor, fontFamily: MONO_FONT }}>
            {highLabel}
          </Text>
        </Box>
      </>
    )
  }

  // The right-justified fallback: whatever the gust trails (a lone high, or
  // the whole "low - high" pair) and the gust itself, as one flowed group
  // flush against the track's right edge. Nothing here is measured — the
  // trail flows inline, so the estimate/measurement dance above is moot.
  const FlushBlock = ({ pair }) => (
    <Box
      style={{
        position: 'absolute',
        left: '100%',
        top: 0,
        transform: 'translateX(-100%)',
        whiteSpace: 'nowrap',
      }}
    >
      <Group gap={TRAIL_GAP} wrap="nowrap" align="baseline">
        {pair ? (
          <Group gap={5} wrap="nowrap" align="baseline">
            <Text fz={DATA_FZ.label} fw={600} style={{ color: lowColor, fontFamily: MONO_FONT }}>
              {lowLabel}
            </Text>
            <Text fz={DATA_FZ.label} fw={600} style={{ color: hyphenColor, fontFamily: MONO_FONT }}>
              {t('range_separator_short')}
            </Text>
            <Text fz={DATA_FZ.label} fw={600} style={{ color: highColor, fontFamily: MONO_FONT }}>
              {highLabel}
            </Text>
          </Group>
        ) : (
          <Text fz={DATA_FZ.label} fw={600} style={{ color: highColor, fontFamily: MONO_FONT }}>
            {highLabel}
          </Text>
        )}
        <GustSuffixText />
      </Group>
    </Box>
  )

  // Build the labels to render beneath the track — up to 3 anchored blocks
  // depending on how much room the day's spread gives. Only one label per
  // render ever carries a trailing suffix, so the single shared measure ref
  // (primaryRef) always targets the right element.
  let labels
  if (lowHighMerge && highGustMerge && blockFlush) {
    labels = <FlushBlock pair />
  } else if (highGustMerge && blockFlush) {
    // Low at its own position, the dash centred in the gap between it and
    // the pulled-in high+gust block (same rule as the unmerged branch below).
    const blockLeftPct = 100 - pxToPct(LABEL_W + TRAIL_GAP + GUST_W)
    const dashCenterPct = (lowAnchor.rightEdgePct + blockLeftPct) / 2
    labels = (
      <>
        <AnchoredLabel anchor={lowAnchor}>
          <Text fz={DATA_FZ.label} fw={600} style={{ color: lowColor, fontFamily: MONO_FONT }}>
            {lowLabel}
          </Text>
        </AnchoredLabel>
        <Box
          style={{
            position: 'absolute',
            left: `${dashCenterPct.toFixed(1)}%`,
            top: 0,
            transform: 'translateX(-50%)',
            whiteSpace: 'nowrap',
          }}
        >
          <Text fz={DATA_FZ.label} fw={600} style={{ color: hyphenColor, fontFamily: MONO_FONT }}>
            {t('range_separator_short')}
          </Text>
        </Box>
        <FlushBlock />
      </>
    )
  } else if (lowHighMerge && highGustMerge) {
    labels = (
      <>
        <Pair anchor={pairAnchor} measureRef={primaryRef} />
        <Box
          style={{
            position: 'absolute',
            left: `${trailLeftPct(pairAnchor, true)}%`,
            top: 0,
            whiteSpace: 'nowrap',
          }}
        >
          <GustSuffixText />
        </Box>
      </>
    )
  } else if (lowHighMerge) {
    labels = (
      <>
        <Pair anchor={pairAnchor} />
        {gustAnchor && (
          <AnchoredLabel anchor={gustAnchor}>
            <GustSuffixText />
          </AnchoredLabel>
        )}
      </>
    )
  } else {
    // The dash centres in the GAP between the two labels, not on the midpoint
    // of the active band. Those are the same point whenever both labels sit
    // where they want to — but a label near either end of the track is clamped
    // inward to stay on it, and then the band's midpoint can fall inside the
    // label itself. A 2 kt low on a 500px track spans 3.6%–11.6% while the
    // midpoint of 2–9 kt is 10%, which is how the dash ended up welded to the
    // low value. Measuring from the rendered edges degrades correctly instead.
    const dashCenterPct = (lowAnchor.rightEdgePct + leftEdgeOf(highAnchor, LABEL_W, trackWidth)) / 2
    labels = (
      <>
        <AnchoredLabel anchor={lowAnchor}>
          <Text fz={DATA_FZ.label} fw={600} style={{ color: lowColor, fontFamily: MONO_FONT }}>
            {lowLabel}
          </Text>
        </AnchoredLabel>
        {/* The dash is present whether or not the pair merged. Here the two
          numbers already sit at their own positions, so it centres on the
          midpoint of the active band — the stretch of track between the
          day's low and high — rather than on either label. */}
        <Box
          style={{
            position: 'absolute',
            left: `${dashCenterPct.toFixed(1)}%`,
            top: 0,
            transform: 'translateX(-50%)',
            whiteSpace: 'nowrap',
          }}
        >
          <Text fz={DATA_FZ.label} fw={600} style={{ color: hyphenColor, fontFamily: MONO_FONT }}>
            {t('range_separator_short')}
          </Text>
        </Box>
        <AnchoredLabel
          anchor={highAnchor}
          measureRef={highGustMerge ? primaryRef : undefined}
          trail={highGustMerge ? <GustSuffixText /> : null}
        >
          <Text fz={DATA_FZ.label} fw={600} style={{ color: highColor, fontFamily: MONO_FONT }}>
            {highLabel}
          </Text>
        </AnchoredLabel>
        {gustAnchor && !highGustMerge && (
          <AnchoredLabel anchor={gustAnchor}>
            <GustSuffixText />
          </AnchoredLabel>
        )}
      </>
    )
  }

  return (
    <Box mt={12}>
      <Group gap={6} align="center" mb={7} wrap="nowrap" justify="center">
        <Text
          fz={DATA_FZ.label}
          fw={700}
          tt="uppercase"
          style={{ letterSpacing: '.06em', color: 'rgba(255,255,255,.75)' }}
        >
          {t('wind_speed')}
        </Text>
        {windDeg != null && (
          <Group gap={4} wrap="nowrap">
            <DirectionArrow deg={windArrowDeg(windDeg)} size={13} />
            <Text fz={DATA_FZ.label} style={{ color: 'rgba(255,255,255,.75)' }}>
              {compassFromDeg(t, windDeg)}
            </Text>
          </Group>
        )}
      </Group>

      <Box
        style={{
          height: 7,
          borderRadius: 4,
          background: trackBg,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {[20, 40, 60, 80].map((pct, i) => (
          <Box
            key={pct}
            style={{
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: `${pct}%`,
              width: 1,
              background: `rgba(255,255,255,${gridColors[i]})`,
            }}
          />
        ))}
        {gustShown && gustRaw > sustRaw && (
          <Box
            style={{
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: `${sustRaw}%`,
              width: `${Math.max(0, gustRaw - sustRaw)}%`,
              borderRadius: 4,
              background: gustHatch(gmax),
            }}
          />
        )}
        <Box
          style={{
            position: 'absolute',
            inset: 0,
            borderRadius: 4,
            background: WIND_SEVERITY_GRADIENT,
            clipPath: `inset(0 ${(100 - sustRaw).toFixed(1)}% 0 ${minRaw.toFixed(1)}% round 4px)`,
          }}
        />
      </Box>

      <Box ref={axisRef} style={{ position: 'relative', height: 18, marginTop: 6 }}>
        {labels}
      </Box>
    </Box>
  )
}
